import { useCallback, useEffect, useRef, useState } from "react";

import { updateAssistantWalkthroughScenario } from "api/services/chat/assistant";
import { NotificationMode, showSnackBar, store } from "store";

import {
  AssistantScenarioStatusEnum,
  AssistantScenarioStatusEnumType,
} from "../../../enums";
import {
  IAssistantFeatureWriteOptions,
  IUseAssistantFeatureWalkthroughOptions,
  IUseAssistantFeatureWalkthroughReturn,
} from "../types";
import { useAssistantFeatureSession } from "./useAssistantFeatureSession";

export const useAssistantFeatureWalkthrough = ({
  identity,
  page,
  onAcknowledge,
  onScenarioCompleted,
}: IUseAssistantFeatureWalkthroughOptions): IUseAssistantFeatureWalkthroughReturn => {
  const [isPending, setIsPending] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);
  const isSaving = useRef(false);

  const session = useAssistantFeatureSession({
    identity,
    page,
    saving: isSaving,
  });
  const {
    feature,
    generation,
    request,
    handleLoad,
    handleProgressSaved,
    handleClose: closeSession,
  } = session;

  const handleReset = useCallback(() => {
    isSaving.current = false;
    setIsPending(false);
    setHasFinished(false);
  }, []);

  useEffect(handleReset, [handleReset, handleLoad]);

  const handleWrite = useCallback(
    async ({
      operation,
      failureMessage,
      onSuccess,
    }: IAssistantFeatureWriteOptions) => {
      if (!feature || isSaving.current) return false;
      const context = generation.current;
      isSaving.current = true;
      request.current += 1;
      setIsPending(true);

      try {
        const result = await operation(feature, context);
        if (context !== generation.current) return false;
        if (result !== true) {
          if (failureMessage)
            store.dispatch(
              showSnackBar({
                message: failureMessage,
                mode: NotificationMode.ERROR,
              })
            );
          return false;
        }
        onSuccess?.();
        return true;
      } catch {
        // Apollo owns ordinary request errors; keep the previous saved status.
        return false;
      } finally {
        if (context === generation.current) {
          isSaving.current = false;
          setIsPending(false);
        }
      }
    },
    [feature, generation, request]
  );

  const handleSave = useCallback(
    (guideId: string, status: AssistantScenarioStatusEnumType) =>
      handleWrite({
        failureMessage: "Scenario progress was not saved.",
        operation: async (currentFeature, context) => {
          const result = await updateAssistantWalkthroughScenario({
            walkthroughFeatureId: currentFeature.id,
            guideId,
            status,
          });
          if (result === true) {
            if (status === AssistantScenarioStatusEnum.COMPLETED)
              void onScenarioCompleted();
            if (context === generation.current)
              await handleProgressSaved(guideId, status);
          }
          return result;
        },
      }),
    [handleWrite, generation, handleProgressSaved, onScenarioCompleted]
  );

  const handleFinish = useCallback(
    () =>
      handleWrite({
        operation: (currentFeature) => onAcknowledge(currentFeature.id),
        onSuccess: () => setHasFinished(true),
      }),
    [handleWrite, onAcknowledge]
  );

  const handleResult = useCallback(
    (guideId: string, skipped: boolean) =>
      handleSave(
        guideId,
        skipped
          ? AssistantScenarioStatusEnum.SKIPPED
          : AssistantScenarioStatusEnum.COMPLETED
      ),
    [handleSave]
  );

  const handleClose = useCallback(() => {
    closeSession();
    handleReset();
  }, [closeSession, handleReset]);

  const handleResume = useCallback(() => setHasFinished(false), []);

  return {
    active: session.active,
    feature,
    isPending,
    hasFinished,
    handleSave,
    handleResult,
    handleFinish,
    handleClose,
    handleResume,
  };
};
